import { ChangeEvent, useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'hooks/useTranslation'
import { Text } from 'components/Text'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { InformationCollapse, InformationCollapseType } from 'components/InformationCollapse'
import { nutritionFormatter } from 'features/Recipes/utils/nutritionFormatter'
import { NUTRITION_PATTIES } from 'fixtures/nutrition'
import { AddToCart } from 'features/ECommerce/hooks/useAddToCart'
import { VenisonBoxId } from 'models/Box'
import { Proteins } from 'models/Pet'
import { ProductVariant } from 'models/Product'
import {
  HeroRoot,
  CopyColumn,
  HeroFlex,
  Title,
  AddToCartContainer,
  RadioWrapper,
  RecipeOption,
  ExtraInformation,
} from '../../../FreezeDriedBox/components/Hero/Hero.styles'
import {
  VenisonRecipesContainer,
  VenisonImageColumn,
  VenisonImageContainer,
} from './Hero.styles'
import venisonBox from '../../../../../../../../public/assets/product-v2/venison-box/venison-box-hero.webp'

type HeroProps = {
  products: ProductVariant[]
  onAdd: AddToCart
}

const pillCss = {
  maxWidth: 'none',
  padding: '0 4px',
  textAlign: 'center',
  fontSize: '13px',
  '@bp1': { fontSize: '$body', padding: '0 $2' },
} as const

const venisonNutrition = nutritionFormatter(NUTRITION_PATTIES[Proteins.VENISON])

const infoList: InformationCollapseType[] = [
  {
    label: 'recipes:hero-v2.safety',
    description: 'recipes:frozen-v2.safety-description',
  },
  {
    label: 'products:essentials.hero.feedingGuidelines.title',
    description: 'products:essentials.hero.feedingGuidelines.description',
  },
  {
    label: 'recipes:hero-v2.ingredients',
    description: 'recipes:ingredients-description',
  },
  {
    label: 'recipes:hero-v2.nutrition-information',
    description: 'recipes:frozen-v2.nutrition-description',
  },
]

export const Hero = ({ products, onAdd }: HeroProps) => {
  const { t } = useTranslation()

  const [quantity, setQuantity] = useState<number>(1)

  const [selectedProduct, setSelectedProduct] = useState<ProductVariant | undefined>(
    () => products.find((product) => product.slug === VenisonBoxId.SMALL) ?? products[0]
  )

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numbersOnly = e.target.value.replace(/\D/g, '')
    const singleNumber = numbersOnly[numbersOnly.length - 1]
    e.target.value = singleNumber
    setQuantity(parseInt(singleNumber) || 1)
  }

  const handleProductSelect = (slug: VenisonBoxId) => {
    const product = products.find((product) => product.slug === slug)
    if (product) {
      setSelectedProduct(product)
    }
  }

  const isBig = selectedProduct?.slug === VenisonBoxId.BIG

  return (
    <HeroRoot id="hero">
      <HeroFlex>
        <CopyColumn>
          <Stack direction="column" gap="3">
            <Title>{t('products-v2:venison-box.hero.title')}</Title>
            <Stack direction="column" gap="1">
              <Text as="p" weight="semiBold">
                {t('products-v2:venison-box.hero.box-contents')}
              </Text>
              <Text as="p">
                {t(
                  isBig
                    ? 'products-v2:venison-box.hero.contents.big'
                    : 'products-v2:venison-box.hero.contents.small'
                )}
              </Text>
            </Stack>
            <Text as="p">{t('products-v2:venison-box.hero.description')}</Text>
          </Stack>
          <VenisonRecipesContainer>
            <Text id="venison-box-select-size-text">
              {t('products-v2:venison-box.hero.select-size')}
            </Text>
            <RadioWrapper role="radiogroup" aria-labelledby="venison-box-select-size-text">
              <RecipeOption
                css={pillCss}
                onClick={() => handleProductSelect(VenisonBoxId.SMALL)}
                isActive={selectedProduct?.slug === VenisonBoxId.SMALL}
                role="radio"
                aria-checked={selectedProduct?.slug === VenisonBoxId.SMALL}
              >
                {t('products-v2:venison-box.hero.small')}
              </RecipeOption>
              <RecipeOption
                css={pillCss}
                onClick={() => handleProductSelect(VenisonBoxId.BIG)}
                isActive={isBig}
                role="radio"
                aria-checked={isBig}
              >
                {t('products-v2:venison-box.hero.big')}
              </RecipeOption>
            </RadioWrapper>
          </VenisonRecipesContainer>
          <AddToCartContainer>
            {selectedProduct && (
              <Text css={{ marginBottom: '$2' }} size="h3">
                ${selectedProduct.price.total / 100}
              </Text>
            )}
            <Stack gap="3" width="fullWidth">
              <input
                id="quantity"
                name="quantity"
                className="fd-box-quantity"
                onChange={handleQuantityChange}
                type="text"
                pattern="\d*"
                value={quantity}
                aria-label="Quantity"
              />
              <Button
                onClick={() =>
                  selectedProduct &&
                  onAdd(
                    selectedProduct.productVariantId,
                    selectedProduct.productId,
                    quantity,
                    selectedProduct.name
                  )
                }
                fullWidth
              >
                {t('products-v2:venison-box.hero.cta')}
              </Button>
            </Stack>
          </AddToCartContainer>
          <ExtraInformation className="d-only">
            <InformationCollapse v2 infoList={infoList} transValues={venisonNutrition} />
          </ExtraInformation>
        </CopyColumn>
        <VenisonImageColumn>
          <VenisonImageContainer>
            <Image priority src={venisonBox} alt={t('products-v2:venison-box.hero.image')} />
          </VenisonImageContainer>
        </VenisonImageColumn>
        <ExtraInformation className="m-only">
          <InformationCollapse v2 infoList={infoList} transValues={venisonNutrition} />
        </ExtraInformation>
      </HeroFlex>
    </HeroRoot>
  )
}

Hero.displayName = 'VenisonBoxHero'
